const xmlEsc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");

/** "example.com/" -> "https://example.com"; returns null when it isn't a usable http(s) origin. */
export function normalizeOrigin(input: string): string | null {
  let v = input.trim();
  if (!v) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) v = "https://" + v;
  try {
    const u = new URL(v);
    if (!/^https?:$/.test(u.protocol) || !u.hostname.includes(".") && u.hostname !== "localhost") return null;
    return u.origin;
  } catch { return null; }
}

export interface RobotsOptions {
  origin: string;
  disallow: string[];
  allowAll: boolean; // false = block the whole site
  crawlDelay?: number;
  blockAiBots: boolean;
}

/** Crawlers blocked by the "Block AI crawlers" option. Some are training crawlers, some fetch pages for AI answers/search. */
export const AI_BOTS = ["GPTBot", "ClaudeBot", "CCBot", "Google-Extended", "PerplexityBot"];

/** The user's LOCAL calendar date as yyyy-mm-dd (toISOString would give the UTC date, which can be tomorrow). */
export function localDateString(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Trim, drop blanks, and prefix a "/" where a rule would otherwise be invalid. "*" patterns are left alone. */
export function normalizeDisallow(lines: string[]): { paths: string[]; fixed: { from: string; to: string }[] } {
  const paths: string[] = [];
  const fixed: { from: string; to: string }[] = [];
  for (const raw of lines) {
    const d = raw.trim();
    if (!d) continue;
    if (d.startsWith("/") || d.startsWith("*")) { paths.push(d); continue; }
    const to = "/" + d;
    fixed.push({ from: d, to });
    paths.push(to);
  }
  return { paths, fixed };
}

export function buildRobots(o: RobotsOptions): string {
  const lines: string[] = [];
  if (!o.allowAll) {
    lines.push("User-agent: *", "Disallow: /");
  } else {
    lines.push("User-agent: *");
    const dis = normalizeDisallow(o.disallow).paths;
    if (dis.length) dis.forEach(d => lines.push(`Disallow: ${d}`));
    else lines.push("Disallow:");
    if (o.crawlDelay && o.crawlDelay > 0) lines.push(`Crawl-delay: ${Math.round(o.crawlDelay)}`);
  }
  if (o.blockAiBots) for (const b of AI_BOTS) lines.push("", `User-agent: ${b}`, "Disallow: /");
  lines.push("", `Sitemap: ${o.origin}/sitemap.xml`);
  return lines.join("\n") + "\n";
}

export interface SitemapOptions { origin: string; pages: string; lastmod: boolean; changefreq: string; priority: string; today: string }

export function buildSitemap(o: SitemapOptions): { xml: string; count: number; skipped: string[]; duplicates: number } {
  const seen = new Set<string>();
  const skipped: string[] = [];
  let duplicates = 0;
  const urls: string[] = [];
  for (const raw of o.pages.split(/\r?\n/)) {
    const p = raw.trim();
    if (!p) continue;
    let loc: string;
    if (/^https?:\/\//i.test(p)) {
      try { const u = new URL(p); if (u.origin !== o.origin) { skipped.push(p); continue; } loc = u.href; } catch { skipped.push(p); continue; }
    } else {
      try { loc = new URL(p.startsWith("/") ? p : "/" + p, o.origin).href; } catch { skipped.push(p); continue; }
    }
    if (seen.has(loc)) { duplicates++; continue; }
    seen.add(loc);
    urls.push(loc);
  }
  const body = urls.map(loc => {
    let e = `  <url>\n    <loc>${xmlEsc(loc)}</loc>`;
    if (o.lastmod) e += `\n    <lastmod>${o.today}</lastmod>`;
    if (o.changefreq) e += `\n    <changefreq>${o.changefreq}</changefreq>`;
    if (o.priority) e += `\n    <priority>${o.priority}</priority>`;
    return e + "\n  </url>";
  }).join("\n");
  const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${body}\n</urlset>\n`;
  return { xml, count: urls.length, skipped, duplicates };
}
